import { fetchCsrfToken } from '@treneo/infrastructure/api';

import { AXIOS_CONSTANTS } from '../axios.constants';

interface QueuedRequest {
  resolve: (value: unknown) => void;
  reject: (reason?: unknown) => void;
}

export class TokenManager {
  private tokenCache: string | null = null;
  private tokenPromise: Promise<string> | null = null;
  private tokenExpiry: number | null = null;
  private queuedRequests: QueuedRequest[] = [];

  public clearCache(): void {
    this.tokenCache = null;
    this.tokenPromise = null;
    this.tokenExpiry = null;
  }

  public async getToken(): Promise<string> {
    const now = Date.now();

    if (this.isTokenValid(now)) {
      const cached = this.tokenCache;
      if (cached) {
        return cached;
      }
    }

    if (this.tokenPromise) {
      return this.tokenPromise;
    }

    return this.fetchNewToken().then(token => token ?? '');
  }

  private isTokenValid(now: number): boolean {
    return !!(
      this.tokenCache &&
      this.tokenExpiry &&
      now < this.tokenExpiry - AXIOS_CONSTANTS.CSRF.REFRESH_BUFFER_MS
    );
  }

  private async fetchNewToken(): Promise<string> {
    this.tokenPromise = this.performTokenFetch();

    try {
      const token = await this.tokenPromise;
      return token;
    } finally {
      this.tokenPromise = null;
    }
  }

  private async performTokenFetch(): Promise<string> {
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), AXIOS_CONSTANTS.CSRF.FETCH_TIMEOUT_MS);

    try {
      const token = await fetchCsrfToken(controller.signal);

      if (token) {
        this.updateTokenCache(token);
      }

      return token;
    } catch (error) {
      if (error instanceof Error && error.name === 'AbortError') {
        throw new Error('CSRF token fetch timeout', { cause: error });
      }
      return '';
    } finally {
      clearTimeout(timeoutId);
    }
  }

  private updateTokenCache(token: string): void {
    this.tokenCache = token;
    this.tokenExpiry = Date.now() + AXIOS_CONSTANTS.CSRF.EXPIRY_MS;
  }

  public processQueue(error: unknown, token: string | null = null): void {
    this.queuedRequests.forEach(request => {
      if (error) {
        request.reject(error);
      } else {
        request.resolve(token);
      }
    });

    this.queuedRequests = [];
  }

  public addToQueue(request: QueuedRequest): void {
    this.queuedRequests.push(request);
  }

  public hasQueuedRequests(): boolean {
    return this.queuedRequests.length > 0;
  }
}
